import { useCallback } from 'react';

import {
  FieldMask,
  PartialFlagsAdminV1SegmentInput,
  SegmentFragment,
  SegmentFragmentDoc,
  SegmentQuery,
  SegmentQueryVariables,
  useOnSegmentUpdatedSubscription,
  useSegmentQuery,
  withStandardGetHook,
} from '@spotify/confidence-graphql';

import { useSegmentMutations } from '../useSegmentMutations';

const useSegmentImpl = withStandardGetHook<
  SegmentQuery, // TData (Full query result type)
  SegmentFragment, // TItem (Type of item at dataPath)
  'segment', // TPath (Path to the item)
  SegmentQueryVariables // TVariables (Query variables type)
>(useSegmentQuery, 'segment');

export const useSegment: typeof useSegmentImpl = options => {
  const result = useSegmentImpl(options);

  useOnSegmentUpdatedSubscription({
    onData: ({ data, client }) => {
      const s = data.data?.segmentUpdated?.segment;
      if (s) {
        const cacheId = client.cache.identify(s);
        if (cacheId) {
          client.cache.writeFragment({
            id: cacheId,
            data: s,
            fragment: SegmentFragmentDoc,
            fragmentName: 'Segment',
          });
        }
      }
    },
    skip: options.skip,
  });

  const {
    createSegment,
    updateSegment: nativeUpdateSegment,
    allocateSegment: nativeAllocateSegment,
    archiveSegment: nativeArchiveSegment,
  } = useSegmentMutations();

  const updateSegment = useCallback(
    async (
      input: PartialFlagsAdminV1SegmentInput,
      updateMask?: FieldMask<PartialFlagsAdminV1SegmentInput>,
    ) => nativeUpdateSegment({ ...input, name: options.name }, updateMask),
    [nativeUpdateSegment, options.name],
  );

  const allocateSegment = useCallback(
    async () => nativeAllocateSegment(options.name),
    [nativeAllocateSegment, options.name],
  );

  const archiveSegment = useCallback(
    async () => nativeArchiveSegment(options.name),
    [nativeArchiveSegment, options.name],
  );

  return {
    ...result,
    createSegment,
    updateSegment,
    allocateSegment,
    archiveSegment,
  };
};
